import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Typography,
  Divider,
  AppBar,
  Toolbar,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutUser } from '../../store/reducers/auth';
import { Billing, Logout, Profile } from '../../assets';
import { stringAvatar } from '../../utils';
import { Wrapper } from '../wrapper';
import { logoutApi } from "../../services/authService";

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 90;

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  display: "flex",
  gap: "1rem",
  padding: theme.spacing(2, 6, 1, 3),
}));

export const Navbar = ({ open, setOpen, dashboardSummary }) => {
  const credit = useSelector((state) => state?.auth?.credits);
  const [anchorEl, setAnchorEl] = useState(null);
  const remainingCredits = dashboardSummary?.creditBalance?.remainingCredits ?? credit ?? "NA";
  const activeMenuLabel = useSelector((state) => state?.ui?.activeMenuLabel);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const role = user?.role;
  const open_menu = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };



  const theme = useTheme();
  const navigate = useNavigate();

  const handleLogout = useCallback(async (currentUser) => {
    await logoutApi(currentUser);
    dispatch(logoutUser());
    navigate("/login");
  }, [dispatch, navigate]);

  const USER_MENU = useMemo(
    () =>
      [
        {
          key: "profile",
          to: "/profile",
          image: Profile,
          label: "Profile",
        },
        role?.toLowerCase() !== "vkyc auditor" && {
          key: "billing",
          to: "/billing",
          image: Billing,
          label: "Billing",
        },
        { key: "divider", divider: true },
        {
          key: "logout",
          onClick: () => handleLogout(user),
          image: Logout,
          label: "Logout",
        },
      ].filter(Boolean),
    [handleLogout, role]
  );

  const { pathname } = useLocation();

  useEffect(() => {
    const handleRouteChange = () => {
      setAnchorEl(null);
    };
    const unlisten = () => {
      return;
    };

    handleRouteChange();
    return unlisten;
  }, [pathname]);

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleStopImpersonation = useCallback(async (currentUser) => {
    try {
      const sourceUrl = sessionStorage.getItem("source");
      sessionStorage.removeItem("source");
      await logoutApi(currentUser);
      dispatch(logoutUser());
      window.location.href = sourceUrl;
    } catch (err) {
      console.error("Failed to stop impersonation", err);
    }
  }, [dispatch, navigate]);

  return (
    <AppBar
      position="fixed"
      sx={{
        background: "#ffffff",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
        zIndex: theme.zIndex.drawer + 1,
        width: `calc(100% - ${open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH}px)`,
        [theme.breakpoints.down("sm")]: {
          width: "100%",
          margin: "0",
          padding: "0",
          zIndex: theme.zIndex.drawer,
        },
        [theme.breakpoints.up("sm")]: {
          width: "100%",
          margin: "0",
          padding: "0",
          zIndex: theme.zIndex.drawer,
        },
      }}
    >
      <Wrapper>
        <Toolbar
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            p: "0px !important",
            minHeight: { xs: "70px", md: "80px" },
            px: { xs: 2, md: 3 },
          }}
        >
          {isMobile && (
            <IconButton
              aria-label="open drawer"
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ mr: 2 }}
            >
              <MenuIcon sx={{ fontSize: "25px", color: "#1976d2" }} />
            </IconButton>
          )}

            {/* 👇 LEFT TEXT AFTER SIDEBAR */}
            <Typography
              variant="h4"
              sx={{
                color: "#020817",
                fontSize: "24px",
                ml: open ? `${DRAWER_WIDTH + 25}px` : `${DRAWER_COLLAPSED_WIDTH + 25}px`,
                transition: "0.3s",
                whiteSpace: "nowrap",
                "@media (max-width: 576px)": {
                    marginLeft: "0px",
                  },
              }}
            >
             {activeMenuLabel}
            </Typography>
            {isMobile && pathname === "/documentation" && (
              <IconButton
                onClick={() => window.dispatchEvent(new CustomEvent("toggle-doc-menu"))}
                sx={{ ml: 0.5 }}
              >
                <MoreVertIcon sx={{ fontSize: "22px", color: "#1976d2" }} />
              </IconButton>
            )}

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={2} alignItems="center">
          {role?.toLowerCase() !== "vkyc auditor" && (
            <Stack direction="row" spacing={1} alignItems="center">
              {user?.isImpersonating && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleStopImpersonation(user)}
                  sx={{
                    height: 32,
                    minHeight: 32,
                    px: isMobile ? 1 : 1.5,
                    minWidth: isMobile ? 32 : "auto",
                    textTransform: "none",
                    fontSize: "12px",
                    borderRadius: 2,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {isMobile ? "Stop" : "Stop Impersonation"}
                </Button>
               )}
                <Chip
                  label={`Credit Balance: ${remainingCredits}`}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontWeight: "medium",
                    fontSize: "12px",
                    display: { xs: "none", sm: "flex" },
                  }}
                />
              </Stack>
            )}
            <Avatar
              sx={{
                bgcolor: theme?.palette?.background?.profileIcon,
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 400,
              }}
              {...stringAvatar(user?.username ? user.username : "", theme)}
              onClick={handleClick}
            />
          </Stack>

          <Menu
            elevation={1}
            id="basic-menu"
            anchorEl={anchorEl}
            open={open_menu}
            onClose={handleClose}
            keepMounted
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            {USER_MENU.map((item) => {
              if (item.divider) {
                return <Divider key={item.key} sx={{ width: "100%" }} />;
              }

              const isAdminBilling =
                item.key === "billing" && user?.role?.toLowerCase() === "admin";
              const isVisible = item.key !== "billing" || isAdminBilling;

              if (isVisible) {
                return (
                  <CustomMenuItem
                    key={item.key}
                    onClick={() => {
                      setAnchorEl(null);
                      item.onClick ? item.onClick() : navigate(item.to);
                    }}
                  >
                    <Box component="img" src={item.image} />
                    <Typography variant="h4">{item.label}</Typography>
                  </CustomMenuItem>
                );
              }

              return null;
            })}
          </Menu>
        </Toolbar>
      </Wrapper>
    </AppBar>
  );
};

Navbar.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  dashboardSummary: PropTypes.shape({
    creditBalance: PropTypes.shape({
      remainingCredits: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
};